<script setup>
import FinancialStatements from "@/tabs/user-dashboard/FinancialStatements.vue";
import MyPortfolio from "@/tabs/user-dashboard/MyPortfolio.vue";
import RecommendationDialog from "@/components/dashboard/RecommendationDialog.vue";
import IntegrateAccountAggregator from "@/components/dashboard/IntegrateAccountAggregator.vue";
import { ref, watchEffect, onMounted } from "vue";
import { useUserStore } from "@/store/user-store";
import { useRouter } from "vue-router";

const selectedTab = ref("");
const isRecommendationDialogOpen = ref(false);
const isAccountAggregatorDialogOpen = ref(false);
const userStore = useUserStore();
const router = useRouter();

const props = defineProps({
  tabs: {
    type: Array,
    default: () => [],
  },
});

watchEffect(() => {
  selectedTab.value = props.tabs.find((tab) => tab.selected).id || "portfolio";
});

// Check authentication and redirect if needed
onMounted(async () => {
  // If no user data in store, redirect to landing page immediately
  if (!userStore.user) {
    console.log('No user data found, redirecting to landing page');
    router.push('/');
    return;
  }

  // If user exists but no dashboard data, try to load it
  if (!userStore.dashboardData && !userStore.isDashboardLoading) {
    console.log('User exists but dashboard data missing, loading...');
    try {
      await userStore.loadDashboardData(userStore.user.email);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // If dashboard load fails, redirect to landing page
      userStore.fireAlert("Error", "Failed to load dashboard data. Please sign in again.");
      setTimeout(() => {
        router.push('/');
      }, 2000);
    }
  }
});

const openRecommendationDialog = () => {
  isRecommendationDialogOpen.value = true;
};

const openAccountAggregatorDialog = () => {
  isAccountAggregatorDialogOpen.value = true;
};

const refreshDashboard = async () => {
  try {
    // Get email from user data
    const email = userStore.user?.email;
    if (email) {
      await userStore.loadDashboardData(email);
    } else {
      console.error('No email found for dashboard refresh');
      userStore.fireAlert("Error", "Unable to refresh dashboard. Please try logging in again.");
    }
  } catch (error) {
    console.error('Failed to refresh dashboard:', error);
    userStore.fireAlert("Error", "Failed to refresh dashboard. Please try again.");
  }
};

</script>

<template>
  <div class="dashboard-container">
    <!-- Loading State -->
    <div v-if="userStore.isDashboardLoading" class="loading-state">
      <i class="pi pi-spinner pi-spin"></i>
      <span>Loading dashboard data...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="!userStore.dashboardData" class="error-state">
      <i class="pi pi-exclamation-triangle"></i>
      <h3>Unable to Load Dashboard</h3>
      <p>Your session may have expired or data is unavailable. Redirecting to sign in...</p>
      <button @click="() => router.push('/')" class="retry-btn">
        <i class="pi pi-sign-in"></i>
        Go to Sign In
      </button>
    </div>

    <!-- Main Content - Only when dashboard data is loaded -->
    <template v-else>
      <template v-if="selectedTab === 'portfolio'">
        <MyPortfolio
          :data="userStore.dashboardData"
          :isLoading="userStore.isDashboardLoading"
          :showPortfolioCommentary="true"
          @open-recommendation="openRecommendationDialog"
          @refresh-dashboard="refreshDashboard"
          @open-account-aggregator="openAccountAggregatorDialog"
        />
      </template>
      <template v-else>
        <FinancialStatements />
      </template>

      <!-- Recommendation Dialog -->
      <RecommendationDialog
        v-model:isRecommendationDialogOpen="isRecommendationDialogOpen"
      />

      <!-- Account Aggregator Dialog -->
      <IntegrateAccountAggregator
        v-model:isAccountAggregatorDialogOpen="isAccountAggregatorDialogOpen"
      />
    </template>
  </div>
</template>

<style scoped>
.dashboard-container {
  position: relative;
  width: 100%;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  color: var(--an-text-secondary, #6b7280);
  min-height: 70vh;
  width: 100%;
}

.loading-state i {
  font-size: 2rem;
  color: var(--an-primary, #0d7c68);
}

.loading-state span {
  font-size: 1rem;
  font-weight: 500;
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  color: var(--an-text-secondary, #6b7280);
  text-align: center;
}

.error-state i {
  font-size: 3rem;
  color: #f59e0b;
}

.error-state h3 {
  font-size: 1.5rem;
  color: var(--an-text-primary, #111827);
  margin: 0;
}

.error-state p {
  font-size: 1rem;
  margin: 0 0 20px 0;
  max-width: 400px;
}

.retry-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--an-primary, #0d7c68);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  background: var(--an-primary-dark, #0a6454);
  transform: translateY(-1px);
}

.retry-btn:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 768px) {
}
</style>
